import React from 'react';

import unescape from 'lodash.unescape';
import sanitizeHtml from 'sanitize-html';

import {withCssModule} from '@pb/kit-util-with-css-module';

import styles from './highlight.component.module.styl';
import {getHighlightedMatches} from './utils';


const classNames = withCssModule(styles);

/**
 * Visual style variants for the highlighted match text.
 *
 * - `DEFAULT` — standard highlight style (background color only).
 * - `BOLD` — bold highlight style. **Deprecated** — the bold variant is no
 *   longer supported by the design system.
 */
export const Type = {
    DEFAULT: 'Type.DEFAULT',
    BOLD: 'Type.BOLD',
};
const TYPE_CLASSES = {
    [Type.DEFAULT]: '',
    [Type.BOLD]: 'highlighted-text_bold',
};

type HighlightStyleType = typeof Type[keyof typeof Type];

/**
 * Describes a single highlighted range within the source `text` string.
 *
 * `startPos` and `endPos` are character-level indices (0-based, exclusive end)
 * into the original `text`. The optional `type` field can carry a `Type` variant
 * string (or any custom string) to style individual matches differently — though
 * the `BOLD` type variant is deprecated.
 */
export type IndexesOfMatches<T = string | undefined> = {
    startPos: number,
    endPos: number,
    type?: T | string | undefined,
};

/**
 * Array of `IndexesOfMatches` objects that define which character ranges in the
 * source `text` should be wrapped in `<mark>` elements.
 *
 * Typically produced by a search/filter function that returns match positions
 * for a query string within a larger text.
 */
export type MatchedChunks = Array<IndexesOfMatches>;

export type Props = {
    /**
     * The full source string to display. Character ranges from `matchedChunks`
     * are wrapped in HTML `<mark>` elements; the rest is rendered as plain text.
     * HTML entities in the string are safely unescaped before rendering.
     */
    text: string,
    /**
     * Highlighted types (default, bold)
     * @deprecated It will be removed in 6 months 09-15-2025. use default style, not the bold one, as it is not supported by the design system
     */
    type?: HighlightStyleType,
    /**
     * Array of character range descriptors that define which parts of `text` to
     * wrap in `<mark>` elements. When omitted or empty, the full `text` is
     * rendered without any highlighting. The output is sanitized with
     * `sanitize-html` to prevent XSS — only `<mark>` tags are allowed through.
     */
    matchedChunks?: MatchedChunks,
    /**
     * When `true`, renders the unescaped `text` value as the native browser
     * tooltip (`title` attribute) on the wrapping `<span>`. Useful when the
     * displayed text may be truncated by the parent container and the full string
     * should be accessible on hover. Defaults to `false`.
     */
    showTitle?: boolean,
};

/**
 * Renders a string with specific character ranges highlighted using the HTML
 * `<mark>` element, typically used to visually emphasize search query matches.
 *
 * Pass the full source string as `text` and a `matchedChunks` array describing
 * which character ranges to wrap in `<mark>`. The rendered HTML is sanitized via
 * `sanitize-html` (only `<mark>` tags allowed) to prevent XSS. HTML entities
 * in the source string are unescaped before rendering.
 *
 * ## Accessibility
 * Highlighted regions use the semantic `<mark>` element, which assistive
 * technologies can announce as highlighted or marked text.
 *
 * @see [Storybook]{@link https://storybook.dev.pitchbookdata.com/?path=/story/text-highlight--highlight-default}
 */

export function Highlight(props: Props) {
    const {
        text,
        matchedChunks,
        type = Type.DEFAULT,
        showTitle = false,
    } = props;

    if (!text) {
        return null;
    }

    const highlightClasses = classNames('highlighted-text', TYPE_CLASSES[type]);

    const __html = sanitizeHtml(getHighlightedMatches(text, matchedChunks), {
        allowedTags: ['mark'],
    });

    return (
        <span
            title={showTitle ? unescape(text) : undefined}
            className={highlightClasses}
            dangerouslySetInnerHTML={{__html}} // eslint-disable-line react/no-danger
        />
    );
}
